import React, { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import useMaterialsByCode from "fe-shared/src/hooks/useMaterialsByCode";
import {
  MaterialGroup,
  MaterialGroupByRegYear,
} from "fe-shared/src/types/domain/Materials";

import getMaterialGroups from "../../helpers/materialsConverter";
import getMaterialGroupsByRegYear from "../../helpers/getMaterialGroupsByRegYear";
import { getFullAddress } from "../../helpers/address";

import {
  InfoRow,
  Transaction,
  TransactionMaterial,
  TransactionStatus,
  TransactionTraversedMaterial,
} from "../../types";

import { DetailsInfoBlock } from "..";

import styles from "./DetailsDataSection.module.scss";

interface Props {
  transaction: Transaction;
  showComparisonMaterials?: boolean;
}

const DispositionDataInfo = ({
  transaction,
  showComparisonMaterials,
}: Props) => {
  const { t } = useTranslation();
  const { materials, traversedMaterials, statusCode } = transaction;
  const address = transaction.sourceWarehouse?.location?.address;
  const isInvalid =
    statusCode === TransactionStatus.Invalid || !traversedMaterials;

  const materialsByCode = useMaterialsByCode();

  const dispositionInfo = useMemo(() => {
    const materialGroups = getMaterialGroups(materials);

    return [
      ...materialGroups.reduce((res, { materialCategoryName, unit, items }) => {
        const infoArray = [
          {
            key: t(materialCategoryName),
            label: `${t(
              `transfers.details.materialTypes.${materialCategoryName}`
            )}, ${t(`units.${unit}`)}`,
            type: "title",
            value: null,
          },
          ...items.map((item) => ({
            key: item.materialCode,
            label: materialsByCode[item.materialCode]?.name,
            value: item.units.unitQuantity.toLocaleString(),
            type: "row",
          })),
        ];

        return res.concat(infoArray);
      }, [] as InfoRow[]),
    ];
  }, [materials, materialsByCode, t]);

  const traversedMaterialsByRegYear = useMemo(
    () => getMaterialGroupsByRegYear(traversedMaterials),
    [traversedMaterials]
  );

  const rowsByRegYearInfo = useMemo(
    () =>
      traversedMaterialsByRegYear.map(
        (
          groupsByRegYear: MaterialGroupByRegYear<TransactionTraversedMaterial>
        ) => {
          const info: InfoRow[] = groupsByRegYear.materialGroups
            .map(
              (g: MaterialGroup<TransactionMaterial>) =>
                [
                  {
                    key: g.materialCategoryName,
                    label: `${t(
                      `transfers.details.materialTypes.${g.materialCategoryName}`
                    )}, ${t(`transfers.details.units.${g.unit}`)}`,
                    value: (g.total || 0).toLocaleString(),
                    type: "title",
                  },
                  ...g.items.map((i: TransactionMaterial) => ({
                    key: i.materialCode,
                    label: i.materialName,
                    value: (i.units.unitQuantity || 0).toLocaleString(),
                    type: "row",
                  })),
                ] as InfoRow[]
            )
            .flat();

          return (
            <Fragment key={`${groupsByRegYear.regYear}-group-reg-year`}>
              <p className={styles.regYearTitle}>
                {groupsByRegYear.regYear
                  ? t("transfers.details.regYear", {
                      regYear: groupsByRegYear.regYear,
                    })
                  : t("transfers.details.unidentifiedRegYear")}
              </p>

              <DetailsInfoBlock
                rowClassName={styles.dispositionInfoTitle}
                rows={info}
              />
            </Fragment>
          );
        }
      ),
    [t, traversedMaterialsByRegYear]
  );

  return (
    <div className={styles.dispositionWrapper}>
      <div className={styles.dispositionStorageInfo}>
        <h4>{t("transactions.details.fromStorage")}</h4>
        <span>{getFullAddress(address)}</span>
      </div>
      <div className={styles.dispositionInformation}>
        {(showComparisonMaterials || isInvalid) && (
          <DetailsInfoBlock
            rows={dispositionInfo}
            rowClassName={classnames(styles.dispositionInfoTitle, {
              [styles.deprecated]: showComparisonMaterials,
            })}
          />
        )}
        {!isInvalid && rowsByRegYearInfo}
      </div>
    </div>
  );
};

export default DispositionDataInfo;
